import React, { lazy, Suspense, useCallback, useEffect, useState } from "react";

import BackgroundConfigByDataset from "components/basic/background-config-by-dataset/BackgroundConfigByDataset";
import { DashboardEditorProps } from "components/ComponentTypeTypes";
import {
  IDatasetSetpointConfig,
  ITableTitles,
} from "components/dashboard/dataset-setpoint-table/common/api/interfaces/interfaces";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { ProgressSpinner } from "primereact/progressspinner";
import { T } from "services/lang/LanguageService";
import { BackgroundColorSetting } from "types/types";
import SetpointTableTitlesEditor from "./setpoint-table-titles-editor/SetpointTableTitlesEditor";

const DatasetSetpointValuesEditor = lazy(
  () => import("./dataset-setpoint-values-editor/DatasetSetpointValuesEditor")
);

const defaultDatasetSetpointConfig: IDatasetSetpointConfig = {
  showTagColumn: true,
  showSetpointColumn: true,
  updateFrequency: 5,
  showTableTitles: true,
  tableTitles: {
    datasetTitle: "",
    datasetValueTitle: "",
    setpointValueTitle: "",
    tagTitle: "",
  },
  values: [],
  compactMode: false,
};

const DatasetSetpointTableEditor = ({
  config,
  onConfigChange,
}: DashboardEditorProps<IDatasetSetpointConfig>) => {
  let conf = config ?? defaultDatasetSetpointConfig;

  if (Object.keys(conf).length === 0) {
    conf = defaultDatasetSetpointConfig;
  }

  const [currentConfig, setCurrentConfig] = useState<IDatasetSetpointConfig>(conf);
  const [isValuesEditorVisible, setValuesEditorVisibility] = useState<boolean>(false);

  const setConfigPropertyState = useCallback((property: keyof IDatasetSetpointConfig, value: any) => {
    setCurrentConfig(prevState => ({ ...prevState, [property]: value }));
  }, []);

  const handleConfigChange = useCallback(
    (id: string, value: any): void => {
      setConfigPropertyState(id as keyof IDatasetSetpointConfig, value);
    },
    [setConfigPropertyState]
  );

  const handleTableTitlesChange = useCallback(
    (id: string, value: any): void => {
      const newConfig = structuredClone(currentConfig);
      if (newConfig) {
        newConfig.tableTitles![id as keyof ITableTitles] = value;
      }
      setCurrentConfig(newConfig);
    },
    [currentConfig]
  );

  const handleBgColorSettingChange = (bgColorSetting: BackgroundColorSetting) => {
    setConfigPropertyState("backgroundColor", bgColorSetting);
  };

  useEffect(() => {
    onConfigChange(currentConfig);
  }, [currentConfig]);

  return (
    <div className="grid p-fluid">
      <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
        <label htmlFor="updateFrequency">{T("load-conf.update-frequency")}</label>
        <div className="p-inputgroup w-full">
          <InputNumber
            id="updateFrequency"
            value={currentConfig.updateFrequency as number}
            onValueChange={e => handleConfigChange(e.target.id, e.target.value)}
          />
          <span className="p-inputgroup-addon">s</span>
        </div>
      </div>
      <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
        <label htmlFor="showTagColumn">{T("config.tag-column")}</label>
        <InputSwitch
          id="showTagColumn"
          type="checkbox"
          checked={Boolean(currentConfig.showTagColumn)}
          onChange={e => handleConfigChange(e.target.id, e.target.value)}
          tooltip={
            currentConfig.showTagColumn
              ? T("message.setpoint-table.tag-column-visible")
              : T("message.setpoint-table.tag-column-hidden")
          }
        />
      </div>
      <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
        <label htmlFor="showSetpointColumn">{T("config.setpoint-column")}</label>
        <InputSwitch
          id="showSetpointColumn"
          type="checkbox"
          checked={Boolean(currentConfig.showSetpointColumn)}
          onChange={e => handleConfigChange(e.target.id, e.target.value)}
          tooltip={
            currentConfig.showSetpointColumn
              ? T("message.setpoint-table.setpoint-column-visible")
              : T("message.setpoint-table.setpoint-column-hidden")
          }
        />
      </div>
      <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
        <label htmlFor="compactMode">{T("config.compact-mode")}</label>
        <InputSwitch
          id="compactMode"
          type="checkbox"
          checked={Boolean(currentConfig.compactMode)}
          onChange={e => handleConfigChange(e.target.id, e.target.value)}
          tooltip={
            currentConfig.compactMode
              ? T("message.setpoint-table.compact-mode-enabled")
              : T("message.setpoint-table.compact-mode-disabled")
          }
        />
      </div>
      <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
        <label htmlFor="showTableTitles">{T("config.table-titles")}</label>
        <InputSwitch
          id="showTableTitles"
          type="checkbox"
          checked={Boolean(currentConfig.showTableTitles)}
          onChange={e => handleConfigChange(e.target.id, e.target.value)}
          tooltip={
            currentConfig.showTableTitles
              ? T("message.table-titles-visible")
              : T("message.table-titles-hidden")
          }
        />
      </div>
      <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
        <Button
          icon="pi pi-list"
          className="component-editor-button icon-blue"
          label={T("word.values")}
          type="button"
          onClick={() => setValuesEditorVisibility(true)}
        />
      </div>

      <Dialog
        header={T("word.values")}
        visible={isValuesEditorVisible}
        style={{ width: "85vw" }}
        onHide={() => setValuesEditorVisibility(false)}
        maximizable
      >
        <Suspense fallback={<ProgressSpinner />}>
          <DatasetSetpointValuesEditor currentConfig={currentConfig} setCurrentConfig={setCurrentConfig} />
        </Suspense>
      </Dialog>
      {currentConfig.showTableTitles && (
        <SetpointTableTitlesEditor
          currentConfig={currentConfig}
          handleTableTitlesChange={handleTableTitlesChange}
        />
      )}
      <div className="mx-2 mt-2 w-full">
        <BackgroundConfigByDataset
          bgColorSetting={currentConfig.backgroundColor}
          setBgColorSetting={handleBgColorSettingChange}
        />
      </div>
    </div>
  );
};

export default DatasetSetpointTableEditor;
